/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {ReactNode, useEffect, useState} from 'react'
import ArgosCollapseComponent from '../../../../../components/CollapseComponent/ArgosCollapseComponent';
import ArgosFlexbox from '../../../../../components/Containers/ArgosFlexbox';
import ArgosTextField from '../../../../../components/Form/TextField/ArgosTextField';
import { Radar } from '../../constants';


interface SensorDescriptionProps {
  sensor: Radar | any
}

/**
 * SensorDescription component is responsible for rendering detailed information about a sensor.
 * It dynamically generates tabs and content based on the type of sensor provided in the props.
 *
 * @param {SensorDescriptionProps} props - The properties object containing sensor data.
 * @param {object} props.sensor - The sensor data object.
 * @param {string} props.sensor.type - The type of the sensor (e.g., "Camera", "Radar", "Lidar").
 *
 * @returns {JSX.Element} The rendered SensorDescription component.
 */
const SensorDescription = (props: SensorDescriptionProps) => {
  const [sensorDescriptionDataMap, setDataMap] = useState<any>({})
  const [sensorDescriptionData, setSensorDescriptionData] = useState(props.sensor)

  useEffect(() => {
    makeTabs()
  }, [])

  const makeTabs = () => {
    let dataMap: object = {}
    if (props.sensor.type === "Camera") {
      setSensorDescriptionData({
        ...props.sensor,
        supplier: props.sensor.cameraSupplier,
        component: props.sensor.lensComponent,
        type: props.sensor.lensType,
        componentVersion: props.sensor.lensVersion
      })
      dataMap = {
        'General Information': processGeneralInfoData(),
        'Field of View': processSRFOVInfoData(),
      }
    } else if (props.sensor.type === "Radar") {
      setSensorDescriptionData({
        ...props.sensor,
        supplier: props.sensor.supplier,
        component: props.sensor.component,
        type: props.sensor.radarType,
        componentVersion: props.sensor.componentVersion
      })
      dataMap = {
        'General Information': processGeneralInfoData(),
        'Sensitivity, Range, Field of View': processSRFOVInfoData(),
        'Link Budget': processLinkBudgetInformation()
      }
    } else if (props.sensor.type === "Lidar") {
      dataMap = {
        'General Information': processGeneralInfoData(),
        'Sensitivity, Range, Field of View': processSRFOVInfoData(),
        'Link Budget': processLinkBudgetInformation(),
        'Reflectivity Improvement': processReflectivityImprovementInformation()
      }
    }
    setDataMap(dataMap)
  }

  function processGeneralInfoData() {
    let supplierField = "supplier"
    let componentNameField = "component"
    let componentTypeField = "radarType"
    let componentVersionField = "componentVersion"

    // Camera specific fields
    if (props.sensor.type === "Camera") {
      supplierField = "cameraSupplier"
      componentNameField = "cameraModule"
      componentVersionField = "version"
    }

    let generalInfoLabelsMapping: object[] = [
      {'Supplier Name': supplierField},
      {'Component Name': componentNameField},
    ]
    if (props.sensor.type !== "Camera") {
      generalInfoLabelsMapping.push({'Component Type': componentTypeField})
    }
    generalInfoLabelsMapping.push({'Component Version/Revision': componentVersionField})

    if (props.sensor.type === "Camera") {
      generalInfoLabelsMapping.push({'Imager Supplier Name': 'imagerSupplier'})
      generalInfoLabelsMapping.push({'Imager Component Name': 'imagerComponent'})
      generalInfoLabelsMapping.push({'Imager Component Type': 'imagerType'})
      generalInfoLabelsMapping.push({'Imager Component Version/Revision': 'imagerVersion'})
    }			
    return mapLabelToSensorValue(generalInfoLabelsMapping)
  }

  function processSRFOVInfoData() {
    if (props.sensor.type === "Radar") {
      const SRFOVInfoLabels = [
        {'Sensor degradation due to manufacturing variation (dB)': 'detectionDegradation'},
        {'Maximum instrumented range (m)': 'maxInstrumentedRange'},
        {'Maximum instrumented field of view - Azimuth (+/- deg)': 'azimuthFOV'},
        {'Maximum instrumented field of view - Elevation (+/- deg)': 'elevationFOV'}
      ]
      return mapLabelToSensorValue(SRFOVInfoLabels)
    } else if (props.sensor.type === "Camera") {
      const SRFOVInfoLabels = [
        {'FOV Degradation Value': 'fovDegredation'},
        {'Maximum instrumented field of view - Azimuth (+/- deg)': 'azimuthResponse'},
        {'Maximum instrumented field of view - Elevation (+/- deg)': 'elevationResponse'}
      ]
      return mapLabelToSensorValue(SRFOVInfoLabels)
    }
  }

  function processLinkBudgetInformation() {
    const linkBudgetInfo = [
      {'Transmit Power at Hot (dBm)': 'txPowerHot'},
      {'Single sided band noise at hot (dB)': 'singleSideBandNoiseHot'},
      {'Two-way antenna gain at hot (dB)': 'twoWayAntannaGainHot'},
      {'Two-way radome and transmission line losses at hot (dB)': 'twoWayRadomeTxLineLossesHot'},
      {'Total number of transmit and receive channels': 'numTxRxChannels'},
      {'Is receiver IQ or real only?': 'isRxIqOrReal'},
      {'Total chirping time per frame/measurement cycle': 'totalChirpTimePerFrame'},
      {'Tx channels transmitted simultaneously in TDM/FDM scheme?': 'txChannelsTransmitSimultaneouslyTdmOrFdm'}
    ]
    return mapLabelToSensorValue(linkBudgetInfo)
  }

  function mapLabelToSensorValue(mappingList: object[]) {
    const returnList: any[] = []
    mappingList.forEach((labelObject: object) => {
      returnList.push({
        'label': Object.keys(labelObject)[0],
        'value': sensorDescriptionData[Object.values(labelObject)[0]]
      })
    })
    const returnComponent: ReactNode = <ArgosFlexbox
      style={{display: "flex", flexFlow: 'row wrap', justifyContent: "center", maxWidth: "90%"}}>
      {returnList.map((mapElement, index) =>
        <ArgosTextField
          key={index}
          style={{width: "600px", gap: "10px 10px"}}
          value={mapElement.value}
          name={mapElement.label}
          id="outlined-basic"
          label={mapElement.label}
          variant="outlined"
          size="small"
          className='grid-item'
          InputProps={{
            readOnly: true,
          }}/>)
      }
    </ArgosFlexbox>

    return returnComponent
  }

  function processReflectivityImprovementInformation() {
    const reflImprovementInfo = [
      {'Improvement in detection range at boresight for a glossy white paint (58%) vs glossy black paint (0.1%) vehicle target.': 'reflectivityImprovement'},
    ]
    return mapLabelToSensorValue(reflImprovementInfo)
  }

  return (
    <ArgosCollapseComponent componentType="sensorDescription" data={sensorDescriptionDataMap}/>
  )
}

export default SensorDescription;
